import {
  BalanceOfAbi,
  HyperbeatPricerReadAbi,
  HyperbeatQueueReadAbi,
  TotalSupplyAbi,
} from '../abis'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * Hyperbeat Vault-Infra reader — six calls per entry:
 *   1. `share.totalSupply()`
 *   2. `pricer.getRate()`                    → underlying-per-share
 *   3. `pricer.decimals()`                   → rate scale (8 today)
 *   4. `underlying.balanceOf(depositReceiver)` → instant-exit inventory
 *   5. `queue.instantWithdrawalFee()`        → base 10 000 ⇒ value IS bps
 *   6. `queue.isInstantWithdrawalPaused()`
 *
 * ## Why not the generic 4626 reader
 *
 * The share token is a bare ERC-20 (a renamed Midas mToken) — `asset()`,
 * `totalAssets()` and `convertToAssets()` all revert. The price lives in
 * the vault's own Pricer (`entry.priceOracle`), a Veda-accountant-shaped
 * `getRate()`/`decimals()` pair; the rate is rescaled here to the
 * package's 1e18 exchange-rate convention. Do NOT use the Midas
 * DepositVault/RedemptionVault the docs list — that stack is retired
 * (paused, NAV feed stale since 2025-12-22); see `abis/hyperbeat.ts`.
 *
 * ## totalAssets
 *
 * Derived as `totalSupply × rate`, rescaled from share decimals (18) to
 * the underlying's raw units (6 for USDT0/USDC, 18 for WHYPE/beHYPE) —
 * there is no on-chain `totalAssets()` to read.
 *
 * ## Liquidity
 *
 * The instant leg (`instantWithdraw`, `withdrawFeeBps` haircut) pays from
 * the vault's **depositReceiver** wallet (`entry.inventoryContract`) —
 * FORK-PROVEN: an instant withdraw succeeded while the queue's own
 * balance read 0, and the payout `transferFrom`ed the depositReceiver.
 * The buffer drains between ops top-ups, so 0 is a normal reading, not
 * an error, and the free `createWithdrawalRequest` leg (processed within
 * 1–3 days, paid out automatically) is the reliable exit. `fetchPublic`
 * clamps the inventory to `totalAssets` and reports it as `liquidity`.
 */
export const readerHyperbeatVault = (
  entry: SavingsRegistryEntry,
): SavingsReader => {
  const { address, underlying, priceOracle, withdrawQueue, inventoryContract } =
    entry
  const pricer = priceOracle ?? address
  const queue = withdrawQueue ?? address
  const shareUnit = 10n ** BigInt(entry.decimals)
  const underlyingUnit =
    10n ** BigInt(entry.underlyingDecimals ?? entry.decimals)
  return {
    calls: [
      { address, name: 'totalSupply', params: [] },
      { address: pricer, name: 'getRate', params: [] },
      { address: pricer, name: 'decimals', params: [] },
      // Falls back to the queue when no depositReceiver is pinned — yields
      // a 0 capacity rather than a malformed call.
      {
        address: underlying,
        name: 'balanceOf',
        params: [inventoryContract ?? queue],
      },
      { address: queue, name: 'instantWithdrawalFee', params: [] },
      { address: queue, name: 'isInstantWithdrawalPaused', params: [] },
    ],
    abis: [
      TotalSupplyAbi,
      HyperbeatPricerReadAbi,
      HyperbeatPricerReadAbi,
      BalanceOfAbi,
      HyperbeatQueueReadAbi,
      HyperbeatQueueReadAbi,
    ],
    parse: ([supply, rate, rateDecimals, inventory, fee, instantPaused]) => {
      const totalSupply = toBigInt(supply)
      const rawRate = toBigInt(rate)
      const rateDec = toBigInt(rateDecimals)
      if (
        totalSupply === undefined ||
        rawRate === undefined ||
        rateDec === undefined ||
        rawRate === 0n
      ) {
        return undefined
      }

      // Normalise the pricer's rate (8 decimals today, read live in case
      // a future pricer scales differently) to the 1e18 convention.
      const exchangeRate =
        rateDec <= 18n
          ? rawRate * 10n ** (18n - rateDec)
          : rawRate / 10n ** (rateDec - 18n)

      const capacity = toBigInt(inventory)
      const feeRaw = toBigInt(fee)

      return {
        // totalSupply is in raw share units; convert to raw underlying.
        totalAssets:
          (totalSupply * exchangeRate * underlyingUnit) / (shareUnit * ONE_E18),
        totalSupply,
        exchangeRate,
        // `instantWithdrawalFee` is over a base of 10 000, so the raw
        // value maps 1:1 onto bps (30 on all four vaults = 0.30% — the
        // docs' 0.5% describes the retired Midas stack).
        withdrawFeeBps: feeRaw !== undefined ? Number(feeRaw) : undefined,
        instantRedeemEnabled:
          typeof instantPaused === 'boolean' ? !instantPaused : undefined,
        instantRedeemCapacity: capacity,
        withdrawQueue: queue.toLowerCase(),
      }
    },
  }
}
